import { defineStore } from 'pinia';
import { ref } from 'vue';
import {
  checkGlobalAdmin,
  checkGlobalAdminStatus,
  claimFirstGlobalAdmin,
  getGlobalAdmins,
  addGlobalAdmin,
  removeGlobalAdmin,
  type GlobalAdminResponse,
  type AddGlobalAdminRequest,
  type IsGlobalAdminResponse
} from '@/api/endpoints/globalAdmin';
import { useNotificationsStore } from '@/store/notificationsStore';

export const useGlobalAdminStore = defineStore('globalAdmin', () => {
  const notificationsStore = useNotificationsStore();

  // State - isGlobalAdmin er null ved ukjent (server-feil), false ved bekreftet ikke-admin
  const isGlobalAdmin = ref<boolean | null>(null);
  const setupSessionId = ref<string | null>(null);
  const globalAdmins = ref<GlobalAdminResponse[]>([]);
  const loading = ref(false);
  const checking = ref(false);
  const error = ref<string | null>(null);
  const hasServerError = ref(false); // Server nede eller 5xx

  /**
   * Sjekk om brukeren er Global Admin
   */
  async function checkIsGlobalAdmin (): Promise<boolean | null> {
    checking.value = true;
    error.value = null;
    hasServerError.value = false;

    try {
      isGlobalAdmin.value = await checkGlobalAdmin();
      return isGlobalAdmin.value;
    } catch (e: any) {
      // Skille mellom server-feil og "bruker er ikke admin"
      if (!e.response || e.response.status >= 500) {
        // Server-feil - vi vet ikke om bruker er admin
        hasServerError.value = true;
        error.value = 'Kunne ikke koble til server';
        isGlobalAdmin.value = null; // Ukjent, ikke false
        return null;
      }
      // Andre feil (403, 404, etc.) - bruker er ikke admin
      error.value = e.message || 'Kunne ikke sjekke Global Admin status';
      isGlobalAdmin.value = false;
      return false;
    } finally {
      checking.value = false;
    }
  }

  /**
   * Sjekk Global Admin status med full respons (inkludert setupSessionId for first-time setup)
   */
  async function checkStatus (): Promise<IsGlobalAdminResponse> {
    checking.value = true;
    error.value = null;
    hasServerError.value = false;

    try {
      const status = await checkGlobalAdminStatus();
      isGlobalAdmin.value = status.isGlobalAdmin;
      setupSessionId.value = status.setupSessionId ?? null;
      return status;
    } catch (e: any) {
      // Skille mellom server-feil og andre feil
      if (!e.response || e.response.status >= 500) {
        // Server-feil - vi vet ikke statusen
        hasServerError.value = true;
        error.value = 'Kunne ikke koble til server';
        isGlobalAdmin.value = null; // Ukjent, ikke false
        setupSessionId.value = null;
        return { isGlobalAdmin: false, setupSessionId: null };
      }
      // Andre feil - bruker er ikke admin
      error.value = e.message || 'Kunne ikke sjekke Global Admin status';
      isGlobalAdmin.value = false;
      setupSessionId.value = null;
      return { isGlobalAdmin: false, setupSessionId: null };
    } finally {
      checking.value = false;
    }
  }

  /**
   * Claim første Global Admin ved first-time setup
   */
  async function claimFirst (): Promise<boolean> {
    if (!setupSessionId.value) {
      notificationsStore.addError('Feil', 'Ingen setupSessionId tilgjengelig');
      return false;
    }

    loading.value = true;
    error.value = null;
    try {
      await claimFirstGlobalAdmin({ setupSessionId: setupSessionId.value });
      setupSessionId.value = null;
      isGlobalAdmin.value = true;
      notificationsStore.addSuccess('GlobalAdmin', 'Du er nå første GlobalAdmin!');
      return true;
    } catch (e: any) {
      const problem = e.response?.data;
      if (problem?.title) {
        notificationsStore.addProblem(problem);
      } else {
        notificationsStore.addError('Feil', 'Kunne ikke sette opp GlobalAdmin.');
      }
      return false;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Hent alle Global Admins
   */
  async function loadGlobalAdmins (): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const admins = await getGlobalAdmins();
      globalAdmins.value = admins;
    } catch (e: any) {
      if (e.response?.status === 403) {
        error.value = 'Du har ikke tilgang til å se GlobalAdmin-listen';
      } else {
        error.value = e.response?.data?.detail || e.message || 'Kunne ikke laste Global Admins';
      }
      notificationsStore.addError('Feil ved lasting', error.value);
    } finally {
      loading.value = false;
    }
  }

  /**
   * Legg til Global Admin
   */
  async function addAdmin (request: AddGlobalAdminRequest): Promise<GlobalAdminResponse | null> {
    loading.value = true;
    error.value = null;
    try {
      const admin = await addGlobalAdmin(request);
      await loadGlobalAdmins(); // Oppdater listen
      notificationsStore.addSuccess('Global Admin lagt til', `${admin.displayName} er nå Global Admin`);
      return admin;
    } catch (e: any) {
      if (e.response?.status === 403) {
        error.value = 'Du har ikke tilgang til å legge til GlobalAdmins';
      } else if (e.response?.status === 409) {
        error.value = e.response?.data?.detail || 'GlobalAdmin eksisterer allerede';
      } else {
        error.value = e.response?.data?.detail || e.message || 'Kunne ikke legge til Global Admin';
      }
      notificationsStore.addError('Feil ved opprettelse', error.value);
      return null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Fjern Global Admin
   */
  async function removeAdmin (objectId: string): Promise<boolean> {
    loading.value = true;
    error.value = null;
    try {
      await removeGlobalAdmin(objectId);
      await loadGlobalAdmins(); // Oppdater listen
      notificationsStore.addSuccess('Global Admin fjernet', 'Global Admin-tilgang er fjernet');
      return true;
    } catch (e: any) {
      if (e.response?.status === 403) {
        error.value = 'Du har ikke tilgang til å fjerne GlobalAdmins';
      } else {
        error.value = e.response?.data?.detail || e.message || 'Kunne ikke fjerne Global Admin';
      }
      notificationsStore.addError('Feil ved fjerning', error.value);
      return false;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Sjekk om en bruker er Global Admin
   */
  function isUserGlobalAdmin (objectId: string): boolean {
    return globalAdmins.value.some(admin => admin.objectId === objectId);
  }

  /**
   * Nullstill GA-tilstand ved identitetsendring (logout / konto-bytte, WI 2255).
   * `isGlobalAdmin = null` = ukjent (samme som initiell), ikke bekreftet ikke-admin.
   */
  function reset (): void {
    isGlobalAdmin.value = null;
    setupSessionId.value = null;
    globalAdmins.value = [];
    loading.value = false;
    checking.value = false;
    error.value = null;
    hasServerError.value = false;
  }

  return {
    // State
    isGlobalAdmin,
    setupSessionId,
    globalAdmins,
    loading,
    checking,
    error,
    hasServerError,
    // Actions
    checkIsGlobalAdmin,
    checkStatus,
    claimFirst,
    loadGlobalAdmins,
    addAdmin,
    removeAdmin,
    isUserGlobalAdmin,
    reset
  };
});

